import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled, { withTheme } from 'styled-components';
import { withTranslation } from 'react-i18next';
import { Brain, Loader2, AlertTriangle as AlertTriangleIcon, Sparkles } from 'common/images/FaIcons';
import FlexContainer from 'common/components/atoms/FlexContainer';
import Button from 'common/components/atoms/Button';
import RBAC from 'common/rbac/RBAC';
import { mapComponentToResource, operations } from 'common/rbac/constants';
import { isVibrationAgentEnabled } from 'common/rbac/util';

// The AI vibration analysis control on an event row: trigger an analysis, watch
// it run, open the result. Shared by the individual feed's ActivityItem and the
// rows inside an expanded Group by Asset group, which had no control at all.

const AIAnalysisContainer = styled(FlexContainer)`
  margin-right: 1em;
`;

const AIAnalysisButton = styled(Button)`
  width: 36px;
  height: 36px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  position: relative;
  
  .rotating {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  
  ${props => props.status === 'completed' && `
    background-color: ${props.theme.primaryColor}10;
    border: 1px solid ${props.theme.primaryColor}20;
    &:hover {
      background-color: ${props.theme.primaryColor}20;
    }
  `}
  
  ${props => props.status === 'pending' && `
    background-color: ${props.theme.primaryColor}10;
    border: 1px solid ${props.theme.primaryColor}20;
    cursor: default;
    &:hover {
      background-color: ${props.theme.primaryColor}10;
    }
  `}
  
  ${props => props.status === 'failed' && `
    background-color: ${props.theme.colors.critical}10;
    border: 1px solid ${props.theme.colors.critical}20;
    &:hover {
      background-color: ${props.theme.colors.critical}20;
    }
  `}
  
  ${props => props.status === 'not-analyzed' && `
    background-color: transparent;
    border: 1px dashed ${props.theme.colors.greyC};
    &:hover {
      background-color: ${props.theme.colors.greyA}10;
      border-color: ${props.theme.colors.greyD};
    }
  `}
  
  svg {
    ${props => props.status === 'completed' && `
      color: ${props.theme.primaryColor};
      transition: transform 0.2s;
    `}
    ${props => props.status === 'pending' && `
      color: ${props.theme.primaryColor};
    `}
    ${props => props.status === 'failed' && `
      color: ${props.theme.colors.critical};
    `}
    ${props => props.status === 'not-analyzed' && `
      color: ${props.theme.colors.greyD};
      transition: color 0.2s;
    `}
  }
  
  &:hover svg {
    ${props => props.status === 'completed' && `
      transform: scale(1.1);
    `}
    ${props => props.status === 'not-analyzed' && `
      color: ${props.theme.primaryColor};
    `}
  }
`;

const AIAnalysisTooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 0.5em;
  padding: 0.5em 0.75em;
  background-color: ${props => props.theme.colors.black};
  color: white;
  font-size: 11px;
  border-radius: 4px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
  z-index: 10;
  
  ${AIAnalysisButton}:hover & {
    opacity: 1;
  }
  
  &:after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: ${props => props.theme.colors.black};
  }
`;

const ICONS = {
  pending: <Loader2 size={16} className="rotating" />,
  completed: <Brain size={16} />,
  failed: <AlertTriangleIcon size={16} />,
  'not-analyzed': <Sparkles size={16} />
};

const tooltipText = (status, t) => {
  switch (status) {
    case 'pending':
      return t('activity.activityItem.aiAnalysis.pending', { defaultValue: 'AI Analysis in Progress...' });
    case 'completed':
      return t('activity.activityItem.aiAnalysis.view', { defaultValue: 'View AI Analysis' });
    case 'failed':
      return t('activity.activityItem.aiAnalysis.failedRetry', { defaultValue: 'Analysis Failed - Click to Retry' });
    case 'not-analyzed':
      return t('activity.activityItem.aiAnalysis.trigger', { defaultValue: 'Trigger AI Analysis' });
    default:
      return '';
  }
};

export const AIAnalysisActionView = (props) => {
  const { activity, metadata, user, currentAccount, onViewAIAnalysis, onTriggerAIAnalysis, t, theme } = props;
  const status = activity.ai_analysis_status;

  if (activity.type !== 'event') return null;
  // Petasense admins always see it (for testing and support).
  if (!isVibrationAgentEnabled(user, currentAccount)) return null;
  const eligibleEventTypes = metadata && metadata.eligibleEventTypes;
  if (eligibleEventTypes && activity.event_type && !eligibleEventTypes.includes(activity.event_type)) {
    return null;
  }
  const icon = ICONS[status];
  if (!icon) return null;

  const onClick = (e) => {
    e.stopPropagation();
    if (status === 'completed' && onViewAIAnalysis) {
      onViewAIAnalysis(activity);
    } else if ((status === 'not-analyzed' || status === 'failed') && onTriggerAIAnalysis) {
      onTriggerAIAnalysis(activity);
    }
  };

  // Triggering needs Create on the vibration agent; viewing a result or a run in
  // progress needs Read.
  const canTrigger = status === 'not-analyzed' || status === 'failed';
  return (
    <RBAC
      resource={mapComponentToResource.VibrationAgent}
      operation={canTrigger ? operations.Create : operations.Read}
      yes={(
        <AIAnalysisContainer>
          <AIAnalysisButton
            text
            status={status}
            onClick={onClick}
            disabled={status === 'pending'}
          >
            {icon}
            <AIAnalysisTooltip theme={theme}>
              {tooltipText(status, t)}
            </AIAnalysisTooltip>
          </AIAnalysisButton>
        </AIAnalysisContainer>
      )}
    />
  );
};

AIAnalysisActionView.propTypes = {
  activity: PropTypes.shape({
    type: PropTypes.string,
    event_type: PropTypes.string,
    ai_analysis_status: PropTypes.oneOf(['pending', 'completed', 'not-analyzed', 'failed'])
  }).isRequired,
  metadata: PropTypes.object,
  user: PropTypes.object,
  currentAccount: PropTypes.object,
  onViewAIAnalysis: PropTypes.func,
  onTriggerAIAnalysis: PropTypes.func,
  t: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired
};

AIAnalysisActionView.defaultProps = {
  metadata: undefined,
  user: undefined,
  currentAccount: undefined,
  onViewAIAnalysis: undefined,
  onTriggerAIAnalysis: undefined
};

const mapStateToProps = state => ({
  user: state.user.user,
  currentAccount: state.currentAccount
});

export default connect(mapStateToProps)(withTheme(withTranslation()(AIAnalysisActionView)));
